import { useMemo, useState } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLoRaDevices } from '../hooks/useLoRaCalibration';
import { DeviceTable } from '../components/DeviceTable';
import { CalibrationDialog } from '../components/CalibrationDialog';
import type { LoRaDevice } from '../types';

const MILESIGHT_PATTERN = /ts30|milesight/i;

function isMilesight(device: LoRaDevice): boolean {
  return device.name != null && MILESIGHT_PATTERN.test(device.name);
}

export default function LoRaCalibrationPage() {
  const { data: devices, isLoading, error, refetch, isRefetching } = useLoRaDevices();
  const [selectedDevEui, setSelectedDevEui] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { milesight, other } = useMemo(() => {
    const ms: LoRaDevice[] = [];
    const ot: LoRaDevice[] = [];
    for (const d of devices ?? []) {
      if (isMilesight(d)) ms.push(d);
      else ot.push(d);
    }
    return { milesight: ms, other: ot };
  }, [devices]);

  const handleCalibrate = (device: LoRaDevice) => {
    setSelectedDevEui(device.devEui);
    setDialogOpen(true);
  };

  // Resolve device from current data to avoid stale references
  const currentDevice = selectedDevEui
    ? devices?.find(d => d.devEui === selectedDevEui) ?? null
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">LoRaWAN Devices</h1>
          <p className="text-muted-foreground">
            Manage and calibrate LoRaWAN devices
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          {isRefetching ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {error ? (
        <div className="text-center py-12">
          <p className="text-destructive mb-2">Failed to load devices</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="milesight">
          <TabsList>
            <TabsTrigger value="milesight">
              Milesight ({milesight.length})
            </TabsTrigger>
            <TabsTrigger value="other">
              Other ({other.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="milesight">
            <DeviceTable
              devices={milesight}
              onCalibrate={handleCalibrate}
              showCalibrate
            />
          </TabsContent>
          <TabsContent value="other">
            <DeviceTable
              devices={other}
              showCalibrate={false}
            />
          </TabsContent>
        </Tabs>
      )}

      <CalibrationDialog
        device={currentDevice}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
